import {
  forwardRef,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';
import { cn } from './cn';

/**
 * Props for the Table root component.
 */
export interface TableProps extends HTMLAttributes<HTMLDivElement> {
  /** Additional CSS classes for the scroll wrapper. */
  className?: string;
}

/**
 * Responsive table wrapper with horizontal scroll on small screens.
 * Use with TableHead, TableBody, TableRow, TableHeader, TableCell.
 *
 * @example
 * ```tsx
 * <Table>
 *   <TableHead>
 *     <TableRow>
 *       <TableHeader>Name</TableHeader>
 *       <TableHeader>Status</TableHeader>
 *     </TableRow>
 *   </TableHead>
 *   <TableBody>
 *     <TableRow>
 *       <TableCell>Alice</TableCell>
 *       <TableCell><Badge variant="success">Active</Badge></TableCell>
 *     </TableRow>
 *   </TableBody>
 * </Table>
 * ```
 */
export function Table({ className, children, ...props }: TableProps) {
  return (
    <div
      className={cn('w-full overflow-x-auto rounded-md border border-ink/10', className)}
      {...props}
    >
      <table className="min-w-full divide-y divide-ink/10">{children}</table>
    </div>
  );
}

/**
 * Props for the TableHead component.
 */
export interface TableHeadProps extends HTMLAttributes<HTMLTableSectionElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * thead section of the Table compound component.
 */
export const TableHead = forwardRef<HTMLTableSectionElement, TableHeadProps>(function TableHead(
  { className, ...props },
  ref,
) {
  return <thead ref={ref} className={cn('bg-card-elevated', className)} {...props} />;
});

/**
 * Props for the TableBody component.
 */
export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * tbody section of the Table compound component.
 */
export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(function TableBody(
  { className, ...props },
  ref,
) {
  return <tbody ref={ref} className={cn('divide-y divide-ink/10 bg-card', className)} {...props} />;
});

/**
 * Props for the TableRow component.
 */
export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * Table row for use inside TableHead or TableBody.
 */
export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(function TableRow(
  { className, ...props },
  ref,
) {
  return <tr ref={ref} className={cn('transition-colors hover:bg-ink/5', className)} {...props} />;
});

/**
 * Props for the TableHeader component.
 */
export interface TableHeaderProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * th element for column headings inside TableHead > TableRow.
 */
export const TableHeader = forwardRef<HTMLTableCellElement, TableHeaderProps>(function TableHeader(
  { className, ...props },
  ref,
) {
  return (
    <th
      ref={ref}
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted',
        className,
      )}
      {...props}
    />
  );
});

/**
 * Props for the TableCell component.
 */
export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * td element for data cells inside TableBody > TableRow.
 */
export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(function TableCell(
  { className, ...props },
  ref,
) {
  return <td ref={ref} className={cn('px-4 py-3 text-sm text-ink', className)} {...props} />;
});
